import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { AppShell, type NavGroup } from "@safrater/ui";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PeopleIcon from "@mui/icons-material/People";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BusinessIcon from "@mui/icons-material/Business";
import ApprovalIcon from "@mui/icons-material/Approval";
import AppsIcon from "@mui/icons-material/Apps";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import PaymentsIcon from "@mui/icons-material/Payments";
import { useAuth } from "../api/auth-context";
import { useInstitutionBackground } from "../hooks/useInstitutionBackground";
import { ImpersonateDialog } from "../pages/users/ImpersonateDialog";
import type { JSX } from "react";


export function ShellLayout() {
  const { user, tenants, moduleAccess, logout, switchTenant } = useAuth();
  const background = useInstitutionBackground("central", user?.selectedTenantId);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  // Central inclui a propria origem no switcher para o usuario voltar para a Central
  // a partir de qualquer satelite. Outros sistemas vem do JWT moduleAccess.
  const centralBaseUrl = `${window.location.protocol}//${window.location.host}/`;
  const systems = [
    { key: "central", label: "Central de Acessos", basePath: "/", baseUrl: centralBaseUrl },
    ...moduleAccess,
  ];
  const navigate = useNavigate();
  const loc = useLocation();
  const path = loc.pathname;
  // Pode gerir usuários/instituições: master global OU admin de ≥1 instituição.
  const canManage = !!user?.isMasterAdmin || (user?.adminInstitutionIds?.length ?? 0) > 0;
  const item = (key: string, label: string, icon: JSX.Element, to: string, exact = false) => ({
    key,
    label,
    icon,
    active: exact ? path === to : path === to || path.startsWith(`${to}/`),
    onClick: () => navigate(to),
  });

  const groups: NavGroup[] = [
    {
      key: "overview",
      label: "Visão geral",
      items: [item("dashboard", "Dashboard", <DashboardIcon />, "/", true)],
    },
    {
      key: "manage",
      label: "Gestão",
      items: [
        // Gestão de usuários/instituições: master OU admin de instituição.
        // Usuários comuns nem veem estes itens.
        ...(canManage ? [item("users", "Usuários", <PeopleIcon />, "/users")] : []),
        ...(canManage ? [item("institutions", "Instituições", <BusinessIcon />, "/institutions")] : []),
        // Organograma: qualquer usuário da instituição pode visualizar.
        item("organograma", "Organograma", <AccountTreeIcon />, "/organograma"),
        ...(user?.isMasterAdmin ? [item("approvals", "Aprovações", <ApprovalIcon />, "/approvals")] : []),
        ...(user?.isMasterAdmin ? [item("audit", "Auditoria", <FactCheckIcon />, "/audit")] : []),
      ],
    },
    {
      key: "catalog",
      label: "Catálogo",
      items: [
        // Módulos é tela de gestão de catálogo → master. Licenças: master gere,
        // admin de instituição lê as suas; usuário comum não vê.
        ...(user?.isMasterAdmin ? [item("modules", "Módulos", <AppsIcon />, "/modules")] : []),
        ...(canManage ? [item("licenses", "Licenças", <VerifiedIcon />, "/licenses")] : []),
        ...(user?.isMasterAdmin ? [item("role-groups", "Grupos de papéis", <GroupWorkIcon />, "/role-groups")] : []),
      ],
    },
    {
      key: "billing",
      label: "Financeiro",
      items: [
        // Pagamentos: master + admin de instituição (pagar/acompanhar as suas).
        // Conta-corrente: endpoints são todos master-only.
        ...(canManage ? [item("payments", "Pagamentos", <PaymentsIcon />, "/payments")] : []),
        ...(user?.isMasterAdmin ? [item("financial", "Conta-corrente", <AccountBalanceIcon />, "/financial")] : []),
      ],
    },
    {
      key: "ajuda",
      label: "Ajuda",
      items: [
        item("manual", "Manual do sistema", <MenuBookIcon />, "/ajuda/manual"),
        item("perguntas", "Perguntas & Respostas", <MenuBookIcon />, "/ajuda/perguntas"),
        item("fluxo", "Fluxo de implantação", <MenuBookIcon />, "/ajuda/fluxo"),
      ],
    },
  ];

  return (
    <AppShell
      appName="Central de Acessos"
      backgroundImageUrl={background?.backgroundImageUrl}
      backgroundFit={background?.backgroundFit}
      backgroundPosition={background?.backgroundPosition}
      backgroundRepeat={background?.backgroundRepeat}
      backgroundOpacity={background?.backgroundOpacity}
      navGroups={groups.filter((g) => g.items.length > 0)}
      tenants={tenants}
      selectedTenantId={user?.selectedTenantId ?? null}
      onTenantChange={switchTenant}
      isMasterAdmin={user?.isMasterAdmin}
      systems={systems}
      currentSystemKey="central"
      user={user ? { name: user.name, email: user.email } : undefined}
      onImpersonate={user?.isMasterAdmin ? () => setImpersonateOpen(true) : undefined}
      onProfile={() => navigate("/profile")}
      onLogout={logout}
    >
      <Outlet />
      <ImpersonateDialog
        open={impersonateOpen}
        currentUserId={user?.sub ?? null}
        onClose={() => setImpersonateOpen(false)}
      />
    </AppShell>
  );
}
